import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from 'src/environments/environment';

import {
  AuthorizeUrl,
  ConnectionList,
  FarmLink,
  FarmsList,
  FieldLink,
  FieldsList,
  GrantUrl,
  LinkFarmRequest,
  LinkFieldRequest,
  LinkMachineRequest,
  OemConnectionsService,
  OemDeereGrantsService,
  OemFarmsService,
  OemFieldsService,
  OemMachine,
  OemMachinesService,
  OemPublicService,
  OemUnmappedEventsService,
  OrgGrant,
  Provider,
  UnlinkFarmResponse,
  UnlinkFieldResponse,
  UnlinkMachineResponse,
  UnmappedEventsResponse,
} from '../api-clients/oem-connection';

/**
 * Field-operation SUMMARY record — the SHIPPED `GET /harvester-field-operations`
 * item shape (SRA003-117), snake_case, exactly the BE `_row_to_dict` keys. The
 * synchronous test-pull (`POST /v2/oem-connection/{provider}/test-pull`,
 * SRA003-404 Test 1) returns this same item shape INLINE, wrapped as
 * `{ items: OemFieldOperation[] }` — the BE computes the summary but does NOT
 * persist stub rows into the audit-tracked canonical table (that is Test 2).
 * The panel renders this response directly; there is no separate read.
 *
 * DISPLAY-ONLY: nothing here feeds harvest calculations. (Fan speed / yield are
 * Phase 2; the OEM machine name comes from the Equipment action's OemMachine,
 * not this row.) Real items also carry raw_response_excerpt and, for the Deere
 * stub, id:0 — extra wire fields are ignored by this display contract.
 */
export interface OemFieldOperation {
  id: number;
  harvester_id: number | null;
  block_id: number | null;
  provider: string;
  event_source_id: string | null;
  operation_started_at: string | null;
  operation_ended_at: string | null;
  total_hours_run: number | null;
  total_fuel_used_l: number | null;
  peak_speed_kmh: number | null;
  avg_engine_load_pct: number | null;
  avg_moisture_pct: number | null;
  avg_ccs: number | null;
  sample_count: number | null;
  // FE-190 Stage 2 / BE PR #276 — Deere harvest-data adapter canonical columns.
  // Always present on the wire, null when the Deere field-ops API doesn't
  // surface the value (fan_speed_rpm currently always null pending SRA003-415).
  cane_yield_tha: number | null;
  ground_speed_kmh: number | null;
  trash_pct: number | null;
  moisture_pct: number | null;
  fan_speed_rpm: number | null;
  // SRA003-519: per-machine last-seen heartbeat on the test-pull diagnostic —
  // max(AEMP /Fleet captured_at, latest canonical-summary activity) for CNH,
  // summary activity only for Deere. ISO-8601, or null when there's no data
  // yet. Drives the freshness chip. Absent on the persisted-telemetry read.
  last_seen?: string | null;
}

/** A Harvest Mate farm the contractor can link an OEM farm to (SRA003-502). */
export interface LinkableFarm {
  id: number;
  ext_id: string;
  name: string;
  grower_name: string | null;
  grower_id: number | null;
  status: string;
  /** Provider currently holding this HM farm, or null when free. */
  linked_provider: string | null;
}

/** A block of a linkable HM farm (SRA003-502). */
export interface LinkableBlock {
  id: number;
  block_number: string;
  status: string;
  linked_provider: string | null;
}

/**
 * Wraps the generated OEM connection client (SRA003-99) with a thin,
 * feature-shaped surface for `/integrations`.
 *
 * Bearer attach: the generated client invokes `Configuration.credentials.cognito`
 * (a `() => string | undefined` hook) on every call via `addCredentialToHeaders`.
 * AppModule wires that hook to read `localStorage` fresh per call - same source
 * as `block.service.ts` but evaluated lazily so it survives re-login. The OEM
 * 401-refresh path (SRA003-139) is handled by `OemTokenRefreshInterceptor`,
 * which only intercepts 401 + `WWW-Authenticate: invalid_token` for
 * `/oem-connection/` URLs. Bearer attach itself stays as the lazy hook - the
 * rest of the codebase still has no interceptor and we don't widen scope here.
 */
@Injectable({ providedIn: 'root' })
export class OemIntegrationService {
  /**
   * Cross-component refresh signal (SRA003-116). Child panels (e.g.
   * field-link-panel after a successful link/unlink) call requestRefresh()
   * so the parent integrations.component re-fetches the connections list +
   * unmapped-events count without threading @Output emitters through
   * manage-view (which doesn't need to know about the refresh).
   */
  private readonly refreshSubject = new Subject<void>();
  readonly connectionsRefresh$ = this.refreshSubject.asObservable();

  /**
   * Farm-linkage change signal (SRA003-502). `farm-link-panel` emits on a
   * successful farm link/unlink so the sibling `field-link-panel` can re-run
   * its Harvest Mate farm pre-fill: a newly-linked farm auto-fills its fields'
   * HM farm, an unlinked farm clears them. Kept SEPARATE from
   * connectionsRefresh$ so the fields panel doesn't reload on unrelated
   * connection / field / machine changes.
   */
  private readonly farmLinksSubject = new Subject<void>();
  readonly farmLinksRefresh$ = this.farmLinksSubject.asObservable();

  /**
   * Field-linkage change signal (SRA003-502, field-first). `field-link-panel`
   * emits when a field link/unlink may have auto-linked or auto-unlinked a
   * parent OEM farm, so the sibling `farm-link-panel` re-fetches its farm list.
   * The mirror of farmLinksRefresh$ — kept as a separate subject so neither
   * panel reacts to its own emissions (no reload loop).
   */
  private readonly fieldLinksSubject = new Subject<void>();
  readonly fieldLinksRefresh$ = this.fieldLinksSubject.asObservable();

  constructor(
    private connectionsApi: OemConnectionsService,
    private machinesApi: OemMachinesService,
    private deereGrantsApi: OemDeereGrantsService,
    private fieldsApi: OemFieldsService,
    private farmsApi: OemFarmsService,
    private unmappedEventsApi: OemUnmappedEventsService,
    private publicApi: OemPublicService,
    private http: HttpClient,
  ) {}

  /** Notify subscribers that connection-related state should be re-fetched. */
  requestRefresh(): void {
    this.refreshSubject.next();
  }

  /**
   * Notify that an OEM farm ↔ Harvest Mate farm linkage changed (SRA003-502),
   * so the field-link panel can re-run its parent-farm pre-fill.
   */
  notifyFarmLinksChanged(): void {
    this.farmLinksSubject.next();
  }

  /**
   * Notify that field links changed and may have auto-linked/unlinked a parent
   * OEM farm (SRA003-502 field-first), so the farm panel re-fetches its list.
   */
  notifyFieldLinksChanged(): void {
    this.fieldLinksSubject.next();
  }

  // ----- Connections -----

  listConnections(): Observable<ConnectionList> {
    return this.connectionsApi.listOemConnections();
  }

  getAuthorizeUrl(provider: Provider): Observable<AuthorizeUrl> {
    return this.connectionsApi.getOemAuthorizeUrl(provider);
  }

  disconnect(provider: Provider): Observable<any> {
    return this.connectionsApi.deleteOemConnection(provider);
  }

  /**
   * Vendor connectivity health check (SRA003-391 / BE #264).
   *
   * POST {apiUrl}oem-connection/{provider}/test → { status, lastChecked }.
   * NOTE: the BE returns status:"ok" (LOWERCASE) on success — callers compare
   * case-insensitively. JWT is read lazily via authHeaders() (see SRA003-208).
   */
  testConnection(provider: Provider): Observable<any> {
    return this.http.post<any>(
      `${this.oemBase(provider)}/test`,
      {},
      { headers: this.authHeaders() },
    );
  }

  // ----- Telemetry diagnostic (SRA003-404 Test 1, display-only) -----

  /**
   * Run a synchronous diagnostic test pull (SRA003-404 Test 1 / BE #264).
   *
   * POST {apiUrl}oem-connection/{provider}/test-pull with an all-optional JSON
   * body (omitted here for the diagnostic). Returns the canonical field-operation
   * summary INLINE as `{ items: OemFieldOperation[] }` — the shipped
   * `GET /harvester-field-operations` shape (SRA003-117); the BE does NOT persist
   * stub rows into the audit-tracked canonical table (that is Test 2). Deere-only
   * today: CNH responds 503 `cnh_unavailable` (the panel shows a friendly
   * notice). Deere stub items carry raw_response_excerpt:{stub:true} + id:0 —
   * synthetic, fine to display.
   */
  triggerTestPull(provider: Provider): Observable<any> {
    return this.http.post<any>(
      `${this.oemBase(provider)}/test-pull`,
      {},
      { headers: this.authHeaders() },
    );
  }

  // ----- Persisted telemetry + DSS subscribe (FE-190 Stage 2 / SRA003-427) -----

  /**
   * List canonical field-operation summary rows (Wave 3 read API, BE #276).
   * `GET {apiUrl}oem-connection/harvester-field-operations` with required
   * half-open `[from, to)` window + optional provider / harvester_id /
   * block_id / limit.
   *
   * MUST be reached via the `oem-connection/` base path, NOT the bare
   * `${apiUrl}` root. The route lives on the OEM HttpApi; API Gateway's
   * custom-domain mappings use longest-prefix match, so `/v2/oem-connection/*`
   * forwards to the OEM api while bare `/v2/*` hits the MAIN api (which has no
   * such route → 404 + phantom CORS). Mirrors `oemBase()` / the dss/subscribe
   * call. Response shape: `{ items: OemFieldOperation[] }` matching the
   * openapi `listHarvesterFieldOperations` operationId.
   */
  listHarvesterFieldOperations(query: {
    from: string;
    to: string;
    provider?: Provider;
    harvester_id?: number;
    block_id?: number;
    limit?: number;
  }): Observable<any> {
    let params = new HttpParams()
      .set('from', query.from)
      .set('to', query.to);
    if (query.provider) {
      params = params.set('provider', query.provider);
    }
    if (query.harvester_id != null) {
      params = params.set('harvester_id', String(query.harvester_id));
    }
    if (query.block_id != null) {
      params = params.set('block_id', String(query.block_id));
    }
    if (query.limit != null) {
      params = params.set('limit', String(query.limit));
    }
    return this.http.get<any>(
      `${environment.apiUrl.replace(/\/$/, '')}/oem-connection/harvester-field-operations`,
      { headers: this.authHeaders(), params },
    );
  }

  /**
   * Register a Deere DSS subscription for fieldOperation events (BE PR #268).
   *
   * `POST {apiUrl}oem-connection/deere/dss/subscribe` — Deere-only by design:
   * DSS is a Deere concept, CNH has no equivalent so callers must not invoke
   * this for non-Deere providers. Idempotent on the BE side per
   * `(contractor, deere, oem_org_id)`. A 503 `deere_unlicensed` means the
   * Deere ag-data license hasn't activated yet (handle as a "pending" toast,
   * not a generic error).
   */
  enableDssSubscribe(oem_org_id: string): Observable<any> {
    return this.http.post<any>(
      `${environment.apiUrl}oem-connection/deere/dss/subscribe`,
      { oem_org_id },
      { headers: this.authHeaders() },
    );
  }

  /**
   * OEM endpoint base for a provider: `${apiUrl}oem-connection/{provider}`
   * (apiUrl already carries the /v2/ surface).
   */
  private oemBase(provider: Provider): string {
    return `${environment.apiUrl.replace(/\/$/, '')}/oem-connection/${provider}`;
  }

  /**
   * Bearer header for the HttpClient-direct OEM test endpoints. The JWT is read
   * from localStorage at CALL time — never cached in the constructor: this
   * service is providedIn:'root' and is instantiated at bootstrap before login,
   * so a constructor-cached header would be a permanent empty Bearer
   * (SRA003-208). Mirrors the generated client's lazy credentials hook; the
   * OemTokenRefreshInterceptor still covers 401 refresh (URL has
   * `/oem-connection/`).
   */
  private authHeaders(): HttpHeaders {
    const token = localStorage.getItem(environment.keys.accessToken) ?? '';
    return new HttpHeaders({ Authorization: `Bearer ${token}` });
  }

  // ----- Linkable HM farms & blocks (SRA003-502) -----

  /**
   * HM farms the contractor can link OEM farms to — the farms reachable via a
   * group they own, each with `linked_provider`. Provider-agnostic: one call
   * feeds both the Deere and CNH pickers. Reached via the `oem-connection/`
   * base (longest-prefix custom-domain mapping), like
   * `listHarvesterFieldOperations`.
   */
  listLinkableFarms(): Observable<any> {
    return this.http.get<any>(
      `${environment.apiUrl.replace(/\/$/, '')}/oem-connection/linkable-farms`,
      { headers: this.authHeaders() },
    );
  }

  /** ACTIVE blocks of one linkable HM farm (by ext_id/UUID), each stamped. */
  listLinkableBlocks(farmExtId: string): Observable<any> {
    return this.http.get<any>(
      `${environment.apiUrl.replace(/\/$/, '')}/oem-connection/linkable-farms/${encodeURIComponent(
        farmExtId,
      )}/blocks`,
      { headers: this.authHeaders() },
    );
  }

  // ----- Machines -----

  listMachines(provider: Provider): Observable<OemMachine[]> {
    return this.machinesApi.listOemMachines(provider);
  }

  linkMachine(
    provider: Provider,
    oemMachineId: string,
    harvesterId: number,
  ): Observable<any> {
    const body: LinkMachineRequest = { harvester_id: harvesterId };
    return this.machinesApi.linkOemMachine(provider, oemMachineId, body);
  }

  /** Clear the machine→harvester linkage (SRA003-440). */
  unlinkMachine(
    provider: Provider,
    oemMachineId: string,
  ): Observable<UnlinkMachineResponse> {
    return this.machinesApi.unlinkOemMachine(provider, oemMachineId);
  }

  // ----- Fields (SRA003-125) -----

  listFields(provider: Provider): Observable<FieldsList> {
    return this.fieldsApi.listOemFields(provider);
  }

  linkField(
    provider: Provider,
    oemFieldId: string,
    blockId: number,
  ): Observable<FieldLink> {
    const body: LinkFieldRequest = { block_id: blockId };
    return this.fieldsApi.linkOemField(provider, oemFieldId, body);
  }

  unlinkField(
    provider: Provider,
    oemFieldId: string,
  ): Observable<UnlinkFieldResponse> {
    return this.fieldsApi.unlinkOemField(provider, oemFieldId);
  }

  // ----- Farms (SRA003-440) -----

  listFarms(provider: Provider): Observable<FarmsList> {
    return this.farmsApi.listOemFarms(provider);
  }

  linkFarm(
    provider: Provider,
    oemFarmId: string,
    farmId: number,
  ): Observable<FarmLink> {
    const body: LinkFarmRequest = { farm_id: farmId };
    return this.farmsApi.linkOemFarm(provider, oemFarmId, body);
  }

  unlinkFarm(
    provider: Provider,
    oemFarmId: string,
  ): Observable<UnlinkFarmResponse> {
    return this.farmsApi.unlinkOemFarm(provider, oemFarmId);
  }

  // ----- Unmapped events (SRA003-116) -----

  listUnmappedEvents(): Observable<UnmappedEventsResponse> {
    return this.unmappedEventsApi.listUnmappedEvents();
  }

  // ----- Deere per-org grants (D9) -----

  listDeereGrants(): Observable<OrgGrant[]> {
    return this.deereGrantsApi.listDeereGrants();
  }

  getDeereGrantUrl(oemOrgId: string): Observable<GrantUrl> {
    return this.deereGrantsApi.getDeereGrantUrl(oemOrgId);
  }

  // ----- Convenience: redirect helpers -----

  /**
   * GET authorize URL then `window.location.href` the browser to the OEM IdP.
   * Verifier stays server-side (D2) - FE does not generate PKCE.
   */
  startConnect(provider: Provider): Observable<string> {
    return this.getAuthorizeUrl(provider).pipe(
      map((res) => {
        if (res?.authorize_url) {
          window.location.href = res.authorize_url;
        }
        return res?.authorize_url ?? '';
      }),
    );
  }

  /**
   * GET grant URL for a Deere org then redirect (D9).
   */
  startDeereGrant(oemOrgId: string): Observable<string> {
    return this.getDeereGrantUrl(oemOrgId).pipe(
      map((res) => {
        if (res?.grant_url) {
          window.location.href = res.grant_url;
        }
        return res?.grant_url ?? '';
      }),
    );
  }
}
